import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Pencil, Trash2, Loader2, AlertCircle, ChevronRight,
  BookOpen, X, CheckCircle2, ToggleLeft, ToggleRight, ShieldCheck,
} from "lucide-react";
import {
  useAdminGetCoursesQuery,
  useAdminCreateCourseMutation,
  useAdminUpdateCourseMutation,
  useAdminDeleteCourseMutation,
} from "../../api/adminExamCoursesApi";
import { PERMS } from "../../../../utils/perms";
import { useAccess } from "../../../../utils/useAccess";

function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? first[0] as string : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? err?.error ?? "An error occurred";
}

type CourseForm = { name: string; code: string; description: string; has_multiple_papers: boolean };
const empty: CourseForm = { name: "", code: "", description: "", has_multiple_papers: false };

export default function AdminExamCoursesPage() {
  const { data: courses = [], isLoading } = useAdminGetCoursesQuery();
  const [createCourse, { isLoading: creating }] = useAdminCreateCourseMutation();
  const [updateCourse, { isLoading: updating }] = useAdminUpdateCourseMutation();
  const [deleteCourse, { isLoading: deleting }] = useAdminDeleteCourseMutation();


  // Reading the course list is exam.access, which opens this route. Creating,
  // editing, retiring and deleting a course is exam.manage.
  const { can } = useAccess();
  const canManage = can.perm(PERMS.exam.manage);
  const [showForm,      setShowForm]      = useState(false);
  const [editTarget,    setEditTarget]    = useState<any | null>(null);
  const [form,          setForm]          = useState<CourseForm>(empty);
  const [formError,     setFormError]     = useState("");
  const [deleteTarget,  setDeleteTarget]  = useState<any | null>(null);
  const [deleteError,   setDeleteError]   = useState("");
  const [msg,           setMsg]           = useState<{ type: "success" | "error"; text: string } | null>(null);

  const set = (k: keyof Pick<CourseForm, "name" | "code" | "description">) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(p => ({ ...p, [k]: e.target.value }));

  const openCreate = () => { setEditTarget(null); setForm(empty); setFormError(""); setShowForm(true); };
  const openEdit   = (c: any) => {
    setEditTarget(c);
    setForm({ name: c.name, code: c.code, description: c.description ?? "", has_multiple_papers: !!c.has_multiple_papers });
    setFormError(""); setShowForm(true);
  };
  const closeForm  = () => { setShowForm(false); setEditTarget(null); };

  const handleSubmit = async () => {
    setFormError("");
    if (!form.name.trim()) { setFormError("Name is required"); return; }
    if (!form.code.trim()) { setFormError("Code is required"); return; }
    try {
      if (editTarget) {
        await updateCourse({ id: editTarget.id, body: form }).unwrap();
        setMsg({ type: "success", text: "Course updated" });
      } else {
        await createCourse(form).unwrap();
        setMsg({ type: "success", text: "Course created" });
      }
      closeForm();
    } catch (err) { setFormError(extractError(err)); }
  };

  const handleToggleActive = async (c: any) => {
    try { await updateCourse({ id: c.id, body: { is_active: !c.is_active } }).unwrap(); }
    catch { /* swallow */ }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError("");
    try {
      await deleteCourse(deleteTarget.id).unwrap();
      setDeleteTarget(null);
      setMsg({ type: "success", text: "Course deleted" });
    } catch (err) { setDeleteError(extractError(err)); }
  };

  const inputCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  const active   = (courses as any[]).filter((c: any) => c.is_active).length;
  const inactive = (courses as any[]).length - active;

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: "Total Courses", val: (courses as any[]).length, icon: BookOpen,     color: "indigo"  },
          { label: "Active",        val: active,                    icon: CheckCircle2, color: "emerald" },
          { label: "Inactive",      val: inactive,                  icon: AlertCircle,  color: "amber"   },
        ].map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <BookOpen className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Exam Courses</h1>
              <p className="text-gray-400 text-sm mt-1.5 font-medium">Master list of courses used across all exam diets.</p>
            </div>
          </div>
          {canManage && <button onClick={openCreate}
            className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all shadow-lg shadow-gray-100 active:scale-95">
            <Plus className="w-4 h-4" /> New Course
          </button>}
        </div>

        {msg && (
          <div className={`mt-6 p-4 rounded-2xl border flex justify-between items-center animate-in fade-in slide-in-from-top-2 ${
            msg.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-800" : "bg-rose-50 border-rose-100 text-rose-800"}`}>
            <span className="text-[10px] font-black uppercase tracking-widest">{msg.text}</span>
            <button onClick={() => setMsg(null)}><X className="w-4 h-4 opacity-50 hover:opacity-100" /></button>
          </div>
        )}
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-600 rounded-xl text-white">
                {editTarget ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </div>
              <div>
                <p className="font-black text-gray-900 text-sm uppercase tracking-tighter">
                  {editTarget ? "Edit Course" : "New Course"}
                </p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  {editTarget ? `Editing ${editTarget.code}` : "Add to master list"}
                </p>
              </div>
            </div>
            <button onClick={closeForm} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          <div className="p-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Course Name *</label>
                <input value={form.name} onChange={set("name")} className={inputCls}
                  placeholder="e.g. Construction Technology" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Course Code *</label>
                <input value={form.code} onChange={set("code")} className={inputCls}
                  placeholder="e.g. QS101" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Description</label>
                <input value={form.description} onChange={set("description")} className={inputCls}
                  placeholder="Optional description" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <div
                    onClick={() => setForm(p => ({ ...p, has_multiple_papers: !p.has_multiple_papers }))}
                    className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${form.has_multiple_papers ? "bg-indigo-600" : "bg-gray-200"}`}
                  >
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.has_multiple_papers ? "translate-x-5" : ""}`} />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-gray-600">Multi-Paper Course</span>
                </label>
              </div>
            </div>

            {formError && (
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {formError}
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <button onClick={handleSubmit} disabled={creating || updating}
                className="bg-gray-900 text-white rounded-2xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all disabled:opacity-30 active:scale-95 inline-flex items-center gap-2">
                {(creating || updating) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {editTarget ? "Save Changes" : "Create Course"}
              </button>
              <button onClick={closeForm}
                className="py-3 px-5 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">
                Discard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="bg-white border border-rose-100 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-50 rounded-xl text-rose-500"><Trash2 className="w-4 h-4" /></div>
            <div>
              <p className="font-black text-gray-900 text-sm uppercase tracking-tighter">Confirm Deletion</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Delete <span className="text-gray-700">{deleteTarget.name}</span>? This cannot be undone.
              </p>
            </div>
          </div>
          {deleteError && (
            <p className="text-[10px] font-black uppercase tracking-widest text-rose-500 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> {deleteError}
            </p>
          )}
          <div className="flex gap-3">
            <button onClick={handleDelete} disabled={deleting}
              className="bg-rose-600 text-white rounded-2xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-rose-700 transition-all disabled:opacity-30 active:scale-95 inline-flex items-center gap-2">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {deleting ? "Deleting…" : "Delete"}
            </button>
            <button onClick={() => { setDeleteTarget(null); setDeleteError(""); }}
              className="py-3 px-5 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Courses table */}
      <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
        <div className="px-8 py-5 border-b border-gray-50 flex items-center justify-between">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
            Course Registry {!isLoading ? `(${(courses as any[]).length})` : ""}
          </div>
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-300" />}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-200" />
          </div>
        ) : (courses as any[]).length === 0 ? (
          <div className="py-24 text-center">
            <BookOpen className="w-10 h-10 mx-auto text-gray-100 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">No courses yet. Create the first one.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-left bg-white border-b border-gray-50">
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Code</th>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Name</th>
                <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] hidden md:table-cell">Description</th>
                <th className="px-8 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Active</th>
                <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(courses as any[]).map((c: any) => (
                <tr key={c.id} className="group hover:bg-gray-50/80 transition-all">
                  <td className="px-8 py-5">
                    <span className="font-black text-indigo-600 text-[10px] uppercase tracking-widest">{c.code}</span>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-black text-gray-900 uppercase tracking-tight">{c.name}</div>
                      {c.has_multiple_papers && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-purple-50 text-purple-700 border border-purple-100">Multi-Paper</span>
                      )}
                    </div>
                  </td>
                  <td className="px-8 py-5 hidden md:table-cell">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest max-w-xs truncate">
                      {c.description ?? "—"}
                    </div>
                  </td>
                  <td className="px-8 py-5 text-center">
                    <button onClick={() => handleToggleActive(c)} disabled={updating || !canManage}
                      className="transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={canManage ? undefined : "You do not have permission to change a course"}>
                      {c.is_active
                        ? <ToggleRight className="w-5 h-5 text-emerald-500 mx-auto" />
                        : <ToggleLeft className="w-5 h-5 text-gray-300 mx-auto" />}
                    </button>
                  </td>
                  <td className="px-8 py-5 text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      {canManage && (
                        <>
                          <button onClick={() => openEdit(c)}
                            className="p-2.5 text-gray-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-xl transition-all">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => { setDeleteTarget(c); setDeleteError(""); }}
                            className="p-2.5 text-gray-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <Link to={`/exams/admin/courses/${c.id}`}
                        className="p-2.5 text-gray-300 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all">
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
          <ShieldCheck className="w-8 h-8 text-indigo-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-2">Course Management</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Courses in this registry are available to all exam diets. Inactive courses cannot be assigned to examiners.
            Deleting a course removes it from all future assignments.
          </p>
        </div>
      </div>
    </div>
  );
}
